import { useEffect, useState } from 'react';
import { PlayerProfile } from '../../shared/analysis/aggregate';
import { ruleColor } from '../../shared/analysis/rulemeta';
import { AnalysisProgress, api } from '../api';
import { formatTimeShort } from '../utils';
import {
  Card, CompareRow, ProgressNote, Segment, ShapeRow, StackBar, pctText, relText, relativeShape, secText,
} from './profile-charts';
import { TechniqueModal, techniqueFor } from './TechniqueModal';
import { RuleId } from '../../shared/rules';

// One player's solving profile, built entirely from attributed replays.
//
// The page is written to be read by the person it describes, which decides two things.
//
// A hunch is a move, not a failure: placing queens speculatively and re-fitting them is measured
// on its own ladder rather than as a deficit of deduction, and the strengths come before the one
// thing worth working on.
//
// And the three comparison cards are **shape cards**, not scoreboards. Each row's ratio to the
// population is divided by the player's own weighted average of those ratios (`relativeShape`),
// so the axis is their own norm and what they read off it is which techniques and which board
// sizes are their strong ones — not eight restatements of a single overall speed. Absolute
// standing is deliberately not on this page; it says nothing a player can act on.
//
// The per-technique speeds are medians over unambiguously attributed single-deduction bursts,
// the softest numbers here, so they carry their sample counts.

export interface ProfileData {
  progress: AnalysisProgress;
  profile: PlayerProfile;
  population: PlayerProfile;
}

interface Props {
  playerKey: string;
  onBack: () => void;
  /** Open the generator on a technique, from inside its modal. */
  onPractice?: (rule: RuleId) => void;
}

const navClick = (cb: () => void) => (e: React.MouseEvent) => {
  if (e.metaKey || e.ctrlKey || e.shiftKey) return;
  e.preventDefault();
  cb();
};

/** Fetches, then hands off to the (pure) body — which is what the tests render. */
export function PlayerProfileView({ playerKey, onBack, onPractice }: Props) {
  const [data, setData] = useState<ProfileData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    const load = () => api.getPlayerProfile(playerKey).then(d => {
      if (!live) return;
      setData(d);
      // The backfill runs in the background the first time anyone opens these pages; keep the
      // numbers moving rather than freezing on a half-analyzed corpus.
      if (d.progress.running) timer = setTimeout(load, 5000);
    }).catch((e: Error) => { if (live) setError(e.message || 'Failed to load'); });
    load();
    return () => { live = false; clearTimeout(timer); };
  }, [playerKey]);

  if (error) return (
    <div className="prof-view">
      <div className="prof-header">
        <a className="link-btn" href="/players" onClick={navClick(onBack)}>← All players</a>
      </div>
      <p className="error">{error}</p>
    </div>
  );
  if (!data) return <p className="loading">Analyzing replays…</p>;
  return (
    <PlayerProfileBody data={data} onBack={onBack} onPractice={onPractice} />
  );
}

/** Says outright that the rows open something. A row that only *looks* tappable is a row most
 *  people never tap — and on a phone there is no hover to discover it with either. */
const TapPrompt = () => (
  <p className="prof-tap-prompt">Tap a technique to see how it works and practise it.</p>
);

export function PlayerProfileBody({ data, onBack, onPractice }: {
  data: ProfileData;
  onBack: () => void;
  onPractice?: (rule: RuleId) => void;
}) {
  const { profile: me, population: all, progress } = data;

  // A row on either technique card opens that technique's Tips & Tricks entry — worked boards and
  // all — rather than sending the reader off the page to find out what "Paired shadow" means.
  // Both cards list only rules that *have* an entry, so every row opens something: R8 is the one
  // that does not, and a forced placement is not a technique anyone needs taught anyway.
  const [openRule, setOpenRule] = useState<string | null>(null);
  const openTechnique = openRule ? techniqueFor(openRule) : undefined;
  // The whole row is the button (see `ShapeRow`), so the name here is styled text, not a nested
  // control — a button inside a button is invalid and the 10px bar would be unhittable anyway.
  const ruleLabel = (rule: string, name: string) => (
    <>
      <span className="rule-dot" style={{ background: ruleColor(rule) }} />
      <span className="prof-rule-link">{name}</span>
    </>
  );
  const ruleRowProps = (rule: string, name: string) => (
    { onClick: () => setOpenRule(rule), openLabel: `${name} — how it works` });

  const queenSegments: Segment[] = [
    { key: 'forced', label: 'Forced by a rule you could see', value: me.queenSplit.forced, color: '#22a06b' },
    { key: 'lookahead', label: 'Deduced without marking the steps', value: me.queenSplit.lookahead, color: '#9333ea' },
    { key: 'changed', label: 'Re-fitted onto a square you had X-ed', value: me.queenSplit.changedMind, color: '#12b5cb' },
    { key: 'commit', label: 'Placed on a hunch', value: me.queenSplit.commit, color: '#e8710a' },
    { key: 'wrong', label: 'Did not fit, and moved', value: me.queenSplit.wrong, color: '#e11d48' },
  ];

  const solves = me.record.solves || me.solves;

  // The three shape cards. Each row is scored against everyone first, then against the player's
  // own average of those scores, so the card reads as a profile rather than as a ranking. Above 1
  // is always the good direction, which is why the two speed cards invert their ratio.
  const usedRules = me.rules.filter(r => r.count > 0 && techniqueFor(r.rule));
  // `share`/`popShare` off the profile are fractions of *every* deduction, including the rules
  // this card does not list — so read straight out they would not add up to what is on screen.
  // Re-based over the listed techniques, the column totals 100% and each row means "this much of
  // your technique work". Both sides are re-based over the same set, so the ratio only moves by a
  // constant, which `relativeShape` divides straight back out: the bars are untouched.
  const shareTotal = usedRules.reduce((a, r) => a + r.count, 0) || 1;
  const popShareTotal = usedRules.reduce((a, r) => a + r.popShare, 0) || 1;
  const shareOf = (r: typeof usedRules[number]) => r.count / shareTotal;
  const popShareOf = (r: typeof usedRules[number]) => r.popShare / popShareTotal;

  const shareShape = relativeShape(usedRules.map(r => ({
    ratio: popShareOf(r) > 0 ? shareOf(r) / popShareOf(r) : 1,
    // Weighted by everyone's share, not the player's: that centers the card on "you solve like
    // the average player", so leaning on a technique shows up as leaning on it.
    weight: popShareOf(r),
  })));

  const timedRules = me.rules.filter(r => r.medianMs !== null && r.samples >= 4 && r.popMedianMs && techniqueFor(r.rule));
  const speedShape = relativeShape(timedRules.map(r => ({
    ratio: r.popMedianMs! / r.medianMs!,
    weight: r.samples,
  })));

  const sizes = me.sizes.filter(s => s.popMedianMs);
  const sizeShape = relativeShape(sizes.map(s => ({
    ratio: s.popMedianMs! / s.medianMs,
    weight: s.solves,
  })));

  const commitWidths = Object.entries(me.commitWidths)
    .map(([w, n]) => ({ w: Number(w), n }))
    .sort((a, b) => a.w - b.w);
  const commitMax = Math.max(1, ...commitWidths.map(c => c.n));

  return (
    <div className="prof-view">
      <div className="prof-header">
        <a className="link-btn" href="/players" onClick={navClick(onBack)}>← All players</a>
      </div>

      {/* Whose page this is, centered above everything it says about them. The solve count is
          the leaderboard's, so it agrees with the roster card; attribution covers slightly fewer
          (a puzzle with no unique solution has no decidable mistakes). */}
      <div className="prof-title">
        <h1>{me.displayName}</h1>
        <div className="prof-dim">
          {solves.toLocaleString()} solve{solves === 1 ? '' : 's'}
          {me.tokens > 1 && <> · {me.tokens} devices merged</>}
          {' · '}{me.facts.toLocaleString()} marks analyzed
        </div>
      </div>

      <ProgressNote done={progress.done} total={progress.total} running={progress.running} />

      {/* Who you are as a solver, before any number gets a chance to read like a grade. */}
      <div className="prof-style">
        <span className="prof-style-label">{me.style.label}</span>
        <span className="prof-style-text">{me.style.text}</span>
      </div>

      {me.highlights.length > 0 && (
        <div className="prof-highlights">
          {me.highlights.map((h, i) => (
            <div key={i} className={`prof-highlight ${h.kind}`}>
              <div className="prof-highlight-label">
                {h.label}
                {h.kind === 'focus' && <span className="prof-focus-tag">next lever</span>}
              </div>
              <div className="prof-highlight-text">{h.text}</div>
            </div>
          ))}
        </div>
      )}

      <div className="prof-grid">
        <Card
          title="Queen decisions"
          hint="How each queen you placed came about. 'Placed on a hunch' means neither the board nor two rounds of unwritten deduction reach that square yet — you got there first."
        >
          <StackBar segments={queenSegments} total={me.queens} />
          {commitWidths.length > 0 && (
            <>
              <div className="prof-sub">When you go on a hunch, how many candidates were left</div>
              {commitWidths.map(c => (
                <CompareRow
                  key={c.w} label={`${c.w} candidate${c.w === 1 ? '' : 's'}`} color="#e8710a"
                  value={c.n} max={commitMax} right={<span className="prof-dim">{c.n}</span>}
                />
              ))}
            </>
          )}
        </Card>

        <Card
          title="Techniques you lean on"
          hint="The simplest rule that explains each of your deductions, compared with everyone else's mix and then re-centerd on your own. Percentages are of the techniques listed here, so they add to 100%. The line down the middle is a player who uses each technique exactly as much as the crowd does."
        >
          {usedRules.length > 0 && <TapPrompt />}
          {usedRules.map((r, i) => (
            <ShapeRow
              key={r.rule}
              label={ruleLabel(r.rule, r.name)}
              {...ruleRowProps(r.rule, r.name)}
              rel={shareShape.rel[i]} span={shareShape.span}
              title={`${pctText(shareOf(r), 0)} of your technique work, against ${pctText(popShareOf(r), 0)} of everyone's`}
              right={
                <span className={shareShape.rel[i] >= 1.05 ? 'prof-good' : 'prof-dim'}>
                  {relText(shareShape.rel[i])}
                  <span className="prof-dim prof-samples"> {pctText(shareOf(r), 0)}</span>
                </span>
              }
            />
          ))}
          {usedRules.length > 0 && (
            <p className="prof-shape-note prof-dim">
              Bars to the right are the techniques you reach for more than most other players.
            </p>
          )}
          {!usedRules.length && <div className="prof-empty">No deductions attributed yet.</div>}
        </Card>

        <Card
          title="Which techniques come quickest to you"
          hint="Median pause before a burst that made exactly one deduction with exactly one explanation — the only bursts where the time can be pinned on a single technique. Each one is scored against everyone's median for it, then against your own average of those scores."
        >
          {timedRules.length > 0 && <TapPrompt />}
          {timedRules.map((r, i) => (
            <ShapeRow
              key={r.rule}
              label={ruleLabel(r.rule, r.name)}
              {...ruleRowProps(r.rule, r.name)}
              rel={speedShape.rel[i]} span={speedShape.span}
              title={`${secText(r.medianMs)} for you, ${secText(r.popMedianMs)} for everyone, over ${r.samples} bursts`}
              right={
                <span className={speedShape.rel[i] >= 1.05 ? 'prof-good' : 'prof-dim'}>
                  {relText(speedShape.rel[i])}
                  <span className="prof-dim prof-samples"> {secText(r.medianMs)} ·{r.samples}</span>
                </span>
              }
            />
          ))}
          {timedRules.length > 0 && (
            <p className="prof-shape-note prof-dim">
              The center is your own pace, not everyone's. Whatever your overall speed, the bars to the
              right are the techniques you see fastest — the ones to build a game around.
            </p>
          )}
          {!timedRules.length && (
            <div className="prof-empty">Not enough cleanly attributed moves yet to time your techniques.</div>
          )}
        </Card>

        <Card
          title="Which board sizes suit you"
          hint="Your median solve time against everyone's, size for size, re-centerd on your own average across the sizes you play."
        >
          {sizes.map((s, i) => (
            <ShapeRow
              key={s.size} label={`${s.size}×${s.size}`}
              rel={sizeShape.rel[i]} span={sizeShape.span}
              title={`${formatTimeShort(s.medianMs)} for you, ${formatTimeShort(s.popMedianMs!)} for everyone, over ${s.solves} solves`}
              right={
                <span className={sizeShape.rel[i] >= 1.05 ? 'prof-good' : 'prof-dim'}>
                  {relText(sizeShape.rel[i])}
                  <span className="prof-dim prof-samples"> {formatTimeShort(s.medianMs)} ·{s.solves}</span>
                </span>
              }
            />
          ))}
          {sizes.length > 0 && (
            <p className="prof-shape-note prof-dim">
              Bars to the right are the sizes you handle better than you handle the rest.
            </p>
          )}
          {!sizes.length && <div className="prof-empty">No solves yet.</div>}
        </Card>
      </div>

      <p className="prof-footnote prof-dim">
        Attribution replays each game against the same rule engine that powers the hints panel, at the
        board state you could actually see. {pctText(me.uniqueAttributionRate, 0)} of your deductions
        pin to exactly one technique; the rest are counted under the simplest rule that covers them.
        Auto-Xs mode is inferred from the replay itself, not from the stored setting. A mark that was
        true of the branch you were exploring is counted as part of that branch rather than as a slip,
        so one speculative queen costs you one decision here, not the dozen marks that followed it.
      </p>

      {openTechnique && (
        <TechniqueModal
          technique={openTechnique}
          onClose={() => setOpenRule(null)}
          onPractice={onPractice}
        />
      )}
    </div>
  );
}
